import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import FileInput, {File} from "../../../../shared/components/FileInput";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {InputSwitch} from "primereact/inputswitch";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import FormsImportDialogFields from "../../field/forms/FormsImportDialogFields";
import MimeType from "../../../document/mimetype/MimeType";
import {Dropdown} from "primereact/dropdown";
import {SelectItem} from "primereact/selectitem";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	FormDocument,
	FormsFormat,
	FormsImport,
	Functions,
	Information,
	MetadataFormsFormat,
	MetadataInformation,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxFormsForms
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {PortalFormsFileData} from "../../../../shared/models/PortalFormsFileData";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import CodeInput from "../../../../shared/components/CodeInput";
import {Base64} from "js-base64";

enum AcceptTypes {
	FDF = "application/vnd.fdf",
	XFDF = "application/vnd.adobe.xfdf",
	XDP = "application/vnd.adobe.xdp+xml",
	XML = "application/xml"
}

export interface FormsImportDialogState extends PortalDialogState<ToolboxFormsForms> {
	importFile: File;
	formType: MetadataFormsFormat;
	filePreview: string;
}

export class FormsImportDialogDefinition extends PortalDialog<ToolboxFormsForms,
	PortalDialogProperties<ToolboxFormsForms>, FormsImportDialogState> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			importFile: {
				name: "",
				mimetype: "",
				value: ""
			},
			formType: MetadataFormsFormat.None,
			fieldErrors: new Map<FormsImportDialogFields, string>(),
			filePreview: ""
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.editForms, Functions.getEditFormsDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.IMPORT_FORMS, signatureEditProfile, Intention.fillingInForms, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}

			if (FunctionHelper.applyValue(
				selectedDocument.metadata?.form?.field, FormDocument.getFieldDefault()
			).length === 0) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderContents(): ReactNode {
		let parameter: ToolboxFormsForms | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let acceptType: AcceptTypes = AcceptTypes.XML
		switch (parameter?._import?.format) {
			case FormsFormat.Fdf:
				acceptType = AcceptTypes.FDF;
				break;
			case FormsFormat.Xdp:
				acceptType = AcceptTypes.XDP;
				break;
			case FormsFormat.Xfdf:
				acceptType = AcceptTypes.XFDF;
				break;
		}

		let formats: Array<SelectItem> = [
			{
				label: Translate.value("import_forms.settings.format.xml", {ns: "dialog"}),
				value: FormsFormat.Xml
			}
		];

		if (this.state.formType === MetadataFormsFormat.AcroForm) {
			formats = formats.concat([
				{
					label: Translate.value("import_forms.settings.format.fdf", {ns: "dialog"}),
					value: FormsFormat.Fdf
				},
				{
					label: Translate.value("import_forms.settings.format.xfdf", {ns: "dialog"}),
					value: FormsFormat.Xfdf
				}
			]);
		}

		if (this.state.formType === MetadataFormsFormat.DynamicXFA ||
			this.state.formType === MetadataFormsFormat.StaticXFA) {
			formats = formats.concat([
				{
					label: Translate.value("import_forms.settings.format.xdp", {ns: "dialog"}),
					value: FormsFormat.Xdp
				}
			]);
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(FormsImportDialogFields.FORMAT)}>
						<label className="p-col-12 p-sm-4">
							<Translate {...this.props} data="import_forms.settings.format.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-format"
									  value={FunctionHelper.applyValue(
										  parameter?._import?.format, FormsImport.getFormatDefault()
									  )}
									  onChange={(e) => this.onChange(
										  FormsImportDialogFields.FORMAT, e.value
									  )}
									  appendTo={document.getElementById("root")} options={formats}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(FormsImportDialogFields.FILE)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-file">
							<Translate {...this.props} data="import_forms.settings.file" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<FileInput inputId="control-file"
									   value={FunctionHelper.applyValue(parameter?._import?.data?.value, "")}
									   filename={this.state.importFile.name} accept={acceptType}
									   onChange={(files) => this.onChange(
										   FormsImportDialogFields.FILE, files[0]
									   )}/>
						</div>
						<div className="p-col-12">
							{!FunctionHelper.isEmpty(this.state.filePreview) &&
								<CodeInput inputId="control-file-content" language={"xml"} height={"300px"}
										   value={this.state.filePreview} hideButtons={true}
										   options={{
											   readOnly: true,
											   contextmenu: false,
											   minimap: {
												   enabled: false
											   }
										   }}/>
							}
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(FormsImportDialogFields.FLATTEN)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-flatten"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-flatten"
										 checked={FunctionHelper.applyValue(
											 parameter?._import?.flatten, FormsImport.getFlattenDefault()
										 )}
										 onChange={(e) => this.onChange(
											 FormsImportDialogFields.FLATTEN, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-flatten">
								<Translate {...this.props} data="import_forms.settings.flatten.label"
										   namespace="dialog"/>
							</label>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["import-forms-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.import_forms.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: FormsImportDialogFields, value: any): void {
		let parameter: ToolboxFormsForms = this.state.parameter!.data;

		switch (field) {
			case FormsImportDialogFields.FORMAT:
				parameter._import!.format = value;

				this.setState({
					parameter: this.state.parameter,
					filePreview: ""
				});
				return;
			case FormsImportDialogFields.FLATTEN:
				parameter._import!.flatten = value;
				break;
			case FormsImportDialogFields.FILE:
				if (!FunctionHelper.exists(value)) {
					return;
				}

				let file: File = value;
				let fileDataType: PortalFormsFileData = PortalFormsFileData.fromJson({
					name: file.name,
					mimetype: file.mimetype,
					value: file.value,
				} as PortalFormsFileData);
				parameter._import!.data = fileDataType;

				this.setState({
					importFile: file,
					parameter: this.state.parameter,
					filePreview: (parameter._import!.format === FormsFormat.Fdf ? "" : Base64.atob(file.value))
				});
				return;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-forms-import small"}/>
					<Translate {...this.props} data="import_forms.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canFillInForm, Security.getCanFillInFormDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: ToolboxFormsForms | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];

		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		let formType: MetadataFormsFormat = FunctionHelper.applyValue(
			portalDocument.metadata?.information?.formType, MetadataInformation.getFormTypeDefault()
		);

		if (
			(
				parameter?._import?.format === FormsFormat.Xdp &&
				formType !== MetadataFormsFormat.DynamicXFA &&
				formType !== MetadataFormsFormat.StaticXFA
			) ||
			(
				(
					parameter?._import?.format === FormsFormat.Fdf ||
					parameter?._import?.format === FormsFormat.Xfdf
				) &&
				formType !== MetadataFormsFormat.AcroForm
			)
		) {
			parameter._import.format = FormsFormat.Xml
		}

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			formType: formType
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			forms: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.forms.import", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.IMPORT_FORMS;
	}

	protected getDefaultParameter(): Parameter<ToolboxFormsForms> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const FormsImportDialog = React.memo(FormsImportDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxFormsForms>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxFormsForms>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default FormsImportDialog;